import { useCallback, useEffect, useReducer, useState } from 'react';
import { set } from 'lodash-es';
import { newId } from '#helpers/helpers.js';

const ACTIONS = {
    ADD_PROFILE: 'add_profile',
    // AFTER_SAVE_PROFILE: 'before_save_profile',
    CLONE_PROFILE: 'clone_profile',
    DELETE_PROFILE: 'delete_profile',
    EDIT_PROFILE: 'edit_profile',
    EDIT_PROFILE_COLORS: 'edit_profile_colors',
    REPLACE_STATE: 'replace_state',
    RESET_PROFILE: 'reset_profile',
    SELECT_PROFILE: 'select_profile'
};

export const makeEmptyProfile = () => ({
    name: 'New Profile',
    companyName: '',
    isDefault: false,
    isNew: true,
    primaryPalette: 'custom',
    neutralPalette: 'custom',
    language: '',
    styles: {
        colors: {
            neutral: {
                50: '#fafafa',
                100: '#f5f5f5',
                200: '#e5e5e5',
                300: '#d4d4d4',
                400: '#a3a3a3',
                500: '#737373',
                600: '#525252',
                700: '#404040',
                800: '#262626',
                900: '#171717',
                950: '#0a0a0a'
            },
            primary: {
                50: '#fdf4ff',
                100: '#fae8ff',
                200: '#f5d0fe',
                300: '#f0abfc',
                400: '#e879f9',
                500: '#d946ef',
                600: '#c026d3',
                700: '#a21caf',
                800: '#86198f',
                900: '#701a75',
                950: '#4a044e'
            }
        },
        themeId: ''
    }
});

function reducer(state, action) {
    switch (action.type) {
        case ACTIONS.REPLACE_STATE: {
            let { profiles, selectedProfile, setSelectedProfileIndex } = action.payload;
            profiles.forEach((el) => (el.isNew = false));
            const sortedProfiles = profiles.sort((a, b) =>
                a.name.toLowerCase() > b.name.toLowerCase() ? 1 : b.name.toLowerCase() > a.name.toLowerCase() ? -1 : 0
            );
            if (selectedProfile) {
                // console.log('selectedProfile', selectedProfile);
                const selectedProfileIndex = sortedProfiles.findIndex((el) => {
                    // Quando creo un profilo nuovo, dopo il refetch, dal server arriva un id diverso da quello temporaneo settato nel front-end, quindi devo confrontare il nome
                    if (el.id === selectedProfile?.id) return true;
                    if (selectedProfile.id.startsWith('NEW')) return el.name === selectedProfile?.name;
                });
                setSelectedProfileIndex(selectedProfileIndex);
            }
            return sortedProfiles;
        }
        case ACTIONS.RESET_PROFILE: {
            const { dbModel, id } = action.payload;
            const updated = structuredClone(state);
            let index = updated.findIndex((el) => el.id === id);
            updated[index] = dbModel;
            return updated;
        }
        case ACTIONS.ADD_PROFILE: {
            const updated = structuredClone(state);
            const newProfile = makeEmptyProfile();
            newProfile.id = newId();
            newProfile.isEditing = true;
            updated.push(newProfile);
            return updated;
        }
        case ACTIONS.SELECT_PROFILE: {
            const { index, setSelectedProfileIndex } = action.payload;
            setSelectedProfileIndex(index);
            return state;
        }
        case ACTIONS.CLONE_PROFILE: {
            const { model, setSelectedProfileIndex } = action.payload;
            const updated = structuredClone(state);
            const newProfile = structuredClone(model);
            newProfile.name = model ? model.name + ' copy' : 'Cloned Profile';
            newProfile.id = newId();
            newProfile.isEditing = true;
            newProfile.isDefault = false;
            setSelectedProfileIndex(updated.length);
            updated.push(newProfile);
            return updated;
        }
        case ACTIONS.DELETE_PROFILE: {
            const { id } = action.payload;
            const updated = structuredClone(state);
            const newState = updated.filter((el) => el.id !== id);
            // const defaultProfileIndex = newState.findIndex((el) => el.isDefault);
            // setSelectedProfileIndex(defaultProfileIndex);
            return newState;
        }
        case ACTIONS.EDIT_PROFILE: {
            const { id, field, data, isStyle } = action.payload;
            const updated = structuredClone(state);
            const profile = updated.find((el) => el.id === id);
            profile.isEditing = true;
            if (field === 'isDefault') updated.forEach((el) => set(el, field, el.id === id));
            if (isStyle) {
                const styles = profile?.styles || {};
                set(styles, field, data);
            } else set(profile, field, data);
            return updated;
        }
        case ACTIONS.EDIT_PROFILE_COLORS: {
            const { id, field, data } = action.payload;
            const updated = structuredClone(state);
            const profile = updated.find((el) => el.id === id);
            const colors = profile?.styles?.colors || {};
            set(colors, field, data);
            return updated;
        }
    }
}

export function useProfiles(initialState, init) {
    const [state, dispatch] = useReducer(reducer, initialState, init);
    const [selectedProfileIndex, setSelectedProfileIndex] = useState(0);
    const selectedProfile = state[selectedProfileIndex];

    useEffect(() => {
        if (initialState.length === 0) return;
        dispatch({
            type: ACTIONS.REPLACE_STATE,
            payload: { profiles: initialState, selectedProfile, setSelectedProfileIndex }
        });
    }, [initialState]);

    const addProfile = useCallback(() => {
        dispatch({
            type: ACTIONS.ADD_PROFILE
        });
    }, []);

    // const afterSaveProfile = useCallback((id) => {
    //     dispatch({
    //         type: ACTIONS.AFTER_SAVE_PROFILE,
    //         payload: { id, setSelectedProfileIndex }
    //     });
    // }, []);

    const cloneProfile = useCallback((model) => {
        dispatch({
            type: ACTIONS.CLONE_PROFILE,
            payload: { model, setSelectedProfileIndex }
        });
    }, []);

    const deleteProfile = useCallback((id) => {
        dispatch({
            type: ACTIONS.DELETE_PROFILE,
            payload: { id, setSelectedProfileIndex }
        });
    }, []);

    const editProfile = useCallback((id, field, data, isStyle) => {
        dispatch({
            type: ACTIONS.EDIT_PROFILE,
            payload: { id, field, data, isStyle }
        });
    }, []);

    const resetProfile = useCallback((id, dbModel) => {
        dispatch({
            type: ACTIONS.RESET_PROFILE,
            payload: { id, dbModel }
        });
    }, []);

    const selectProfile = useCallback((index) => {
        dispatch({
            type: ACTIONS.SELECT_PROFILE,
            payload: { index, setSelectedProfileIndex }
        });
    }, []);

    const editProfileColors = useCallback((id, field, data) => {
        dispatch({
            type: ACTIONS.EDIT_PROFILE_COLORS,
            payload: { id, field, data }
        });
    }, []);

    return {
        profiles: state,
        selectedProfile,
        selectedProfileIndex,
        addProfile,
        // afterSaveProfile,
        deleteProfile,
        editProfile,
        editProfileColors,
        cloneProfile,
        resetProfile,
        selectProfile
    };
}
